import React from "react";
import {Loader2} from "lucide-react";
import {clsx} from "clsx";
import type {ButtonProps} from "../../types";

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      loading = false,
      disabled = false,
      onClick,
      type = "button",
      children,
      className = "",
      ...props
    },
    ref
  ) => {
    const baseStyles =
      "inline-flex items-center justify-center font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

    const variants = {
      primary:
        "bg-primary-600 hover:bg-primary-700 text-white focus:ring-primary-500",
      secondary:
        "bg-gray-100 hover:bg-gray-200 text-gray-900 dark:bg-dark-700 dark:hover:bg-dark-600 dark:text-gray-100 focus:ring-gray-500",
      danger:
        "bg-danger-600 hover:bg-danger-700 text-white focus:ring-danger-500",
      ghost:
        "bg-transparent hover:bg-gray-100 text-gray-700 dark:hover:bg-dark-700 dark:text-gray-300 focus:ring-gray-500",
      outline:
        "border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 dark:border-dark-600 dark:bg-dark-800 dark:hover:bg-dark-700 dark:text-gray-300 focus:ring-primary-500",
    };

    const sizes = {
      sm: "px-3 py-1.5 text-sm",
      md: "px-4 py-2 text-sm",
      lg: "px-6 py-3 text-base",
    };

    return (
      <button
        ref={ref}
        type={type}
        onClick={onClick}
        disabled={disabled || loading}
        className={clsx(baseStyles, variants[variant], sizes[size], className)}
        {...props}
      >
        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
